import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, Calendar, CreditCard, Edit, Eye, EyeOff, Save, X } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { agencyAPI, authAPI, billingAPI, profileAPI } from '../api/index';
import { queryClient } from '../App';
import { Button } from '../components/Button';
import { PageHeader } from '../components/PageHeader';
import { formatDate } from '../lib/utils';
import { validateEmail, validatePassword, validatePhone } from '../lib/validation';
import { useAuthStore } from '../store/auth';

export function SettingsPage() {
  const user = useAuthStore(s => s.user)
  const isAdmin = user?.role === 'admin'

  const { data: agency, isLoading: agencyLoading } = useQuery({
    queryKey: ['agency'],
    queryFn: () => agencyAPI.get().then(r => r.data),
    enabled: isAdmin,
  })

  const { data: billingStatus, isLoading: billingLoading } = useQuery({
    queryKey: ['billing-status'],
    queryFn: () => billingAPI.getStatus().then(r => r.data),
  })

  const [agencyForm, setAgencyForm] = useState({
    name: '',
    license_number: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    logo_url: '',
  })

  const [profileForm, setProfileForm] = useState({
    full_name: user?.full_name || '',
    avatar_url: user?.avatar_url || '',
  })

  const [editingSection, setEditingSection] = useState<'agency' | 'profile' | null>(null)
  const [prefilledLicenseNumber, setPrefilledLicenseNumber] = useState<string | null>(null)
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [passwordForm, setPasswordForm] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  })
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false,
  })
  const [showPaymentModal, setShowPaymentModal] = useState(false)

  // Prefill agency form when data is loaded
  useEffect(() => {
    if (agency) {
      setPrefilledLicenseNumber(agency.license_number || null)
      setAgencyForm({
        name: agency.name || "",
        license_number: agency.license_number || "",
        phone: agency.phone || "",
        email: agency.email || "",
        address: agency?.address || "",
        city: agency.city || "",
        state: agency.state || "",
        pincode: agency?.pincode || "",
        logo_url: agency.logo_url || "",
      });
    }
  }, [agency])

  const updateAgencyMutation = useMutation({
    mutationFn: (data: any) => agencyAPI.update(data),
    onSuccess: () => {
      toast.success('Agency settings updated')
      setEditingSection(null)
      queryClient.invalidateQueries({ queryKey: ['agency'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error ?? 'Failed to update')
    },
  })

  const updateProfileMutation = useMutation({
    mutationFn: (data: any) => profileAPI.updateMe(data),
    onSuccess: () => {
      toast.success('Profile updated')
      setEditingSection(null)
      queryClient.invalidateQueries({ queryKey: ['profile'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error ?? 'Failed to update')
    },
  })

  const changePasswordMutation = useMutation({
    mutationFn: (data: { old_password: string; new_password: string }) =>
      authAPI.changePassword(data.old_password, data.new_password),
    onSuccess: () => {
      toast.success('Password changed successfully')
      setShowPasswordForm(false)
      setPasswordForm({ old_password: '', new_password: '', confirm_password: '' })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error ?? 'Failed to change password')
    },
  })

  const handleAgencySubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate phone if provided
    if (agencyForm.phone && !validatePhone(agencyForm.phone)) {
      toast.error('Please enter a valid phone number (10 digits)')
      return
    }

    // Validate email if provided
    if (agencyForm.email && !validateEmail(agencyForm.email)) {
      toast.error('Please enter a valid email address')
      return
    }

    // Send form data as-is, backend will handle COALESCE logic
    updateAgencyMutation.mutate(agencyForm)
  }

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    updateProfileMutation.mutate({
      full_name: profileForm.full_name,
      avatar_url: profileForm.avatar_url,
    })
  }

  const handleCancelEdit = () => {
    setEditingSection(null)
    // Reload agency form from current data
    if (agency) {
      setAgencyForm({
        name: agency.name || '',
        license_number: agency.license_number || '',
        phone: agency.phone || '',
        email: agency.email || '',
        address: agency.address || '',
        city: agency.city || '',
        state: agency.state || '',
        pincode: agency.pincode || '',
        logo_url: agency.logo_url || '',
      })
    }
    // Reload profile form from current user data
    setProfileForm({
      full_name: user?.full_name || '',
      avatar_url: user?.avatar_url || '',
    })
  }

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Validate old password
    if (!passwordForm.old_password) {
      toast.error('Please enter your current password')
      return
    }

    // Validate new password
    if (!passwordForm.new_password) {
      toast.error('Please enter a new password')
      return
    }

    if (!validatePassword(passwordForm.new_password)) {
      toast.error('New password does not meet requirements')
      return
    }

    // Validate confirm password
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match')
      return
    }

    // Same as old password
    if (passwordForm.old_password === passwordForm.new_password) {
      toast.error('New password cannot be the same as current password')
      return
    }

    // Submit
    changePasswordMutation.mutate({
      old_password: passwordForm.old_password,
      new_password: passwordForm.new_password,
    })
  }

  return (
    <div>
      <PageHeader title="Settings" />

      <div className="max-w-2xl space-y-8">
        {/* Subscription & Trial Details */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Subscription & Trial</h2>
          </div>

          {billingLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-4 bg-blue-100 rounded animate-pulse" />
              ))}
            </div>
          ) : billingStatus ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-blue-600 font-medium uppercase mb-1">Current Plan</p>
                  <p className="text-lg font-semibold text-gray-900">{billingStatus.plan_name}</p>
                </div>
                <div>
                  <p className="text-xs text-blue-600 font-medium uppercase mb-1">Price</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {billingStatus.price_display}
                    <span className="text-sm text-gray-600 font-normal">/month</span>
                  </p>
                </div>
              </div>

              <div className="flex gap-2 items-center">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  billingStatus.subscription_status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : billingStatus.subscription_status === 'trialing'
                    ? 'bg-blue-100 text-blue-800'
                    : billingStatus.subscription_status === 'past_due'
                    ? 'bg-orange-100 text-orange-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {billingStatus.subscription_status === 'trialing'
                    ? 'Trial Active'
                    : billingStatus.subscription_status.charAt(0).toUpperCase() + billingStatus.subscription_status.slice(1)}
                </span>
                <span className="text-sm font-medium text-gray-600">
                  {billingStatus.days_remaining > 0 && `${billingStatus.days_remaining} days remaining`}
                </span>
              </div>

              <div>
                <p className="text-sm text-gray-700 mb-2">
                  {billingStatus.subscription_status === 'trialing'
                    ? `Trial Period End: ${formatDate(billingStatus.trial_ends_at)}`
                    : billingStatus.subscription_ends_at
                    ? `Subscription End: ${formatDate(billingStatus.subscription_ends_at)}`
                    : 'Active subscription'}
                </p>
              </div>

              <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-blue-600 h-full transition-all"
                  style={{
                    width: `${Math.max(
                      0,
                      Math.min(100, (billingStatus.days_remaining / 30) * 100)
                    )}%`,
                  }}
                />
              </div>

              {billingStatus.subscription_status !== 'active' && billingStatus.subscription_status !== 'trialing' && (
                <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-orange-800">
                    Your subscription requires attention. Please complete the payment to activate your agency.
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowPaymentModal(true);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition font-medium"
                >
                  <CreditCard className="w-4 h-4" />
                  Pay Now
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {/* Agency Settings (Admin Only) */}
        {isAdmin && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Agency Profile</h2>
              {editingSection !== 'agency' && (
                <button
                  onClick={() => setEditingSection('agency')}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
              )}
            </div>

            {agencyLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />
                ))}
              </div>
            ) : editingSection === 'agency' ? (
              <form onSubmit={handleAgencySubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Agency Name*</label>
                    <input
                      type="text"
                      value={agencyForm.name}
                      onChange={(e) => setAgencyForm({ ...agencyForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-saffron focus:outline-none"
                      placeholder="Enter agency name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">License Number</label>
                    <input
                      type="text"
                      value={agencyForm.license_number || ''}
                      onChange={(e) => setAgencyForm({ ...agencyForm, license_number: e.target.value })}
                      disabled={agencyForm.license_number === prefilledLicenseNumber && !!prefilledLicenseNumber}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-saffron focus:outline-none ${
                        agencyForm.license_number === prefilledLicenseNumber && !!prefilledLicenseNumber
                          ? 'bg-gray-50 text-gray-600 cursor-not-allowed'
                          : ''
                      }`}
                      placeholder="Enter license number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mobile No</label>
                    <input
                      type="tel"
                      value={agencyForm.phone}
                      onChange={(e) => setAgencyForm({ ...agencyForm, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-saffron focus:outline-none"
                      placeholder="+91 98765 43210 or 9876543210"
                    />
                    {agencyForm.phone && !validatePhone(agencyForm.phone) && (
                      <p className="mt-1 text-xs text-red-600">Invalid phone number. Use 10 digits.</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={agencyForm.email}
                      onChange={(e) => setAgencyForm({ ...agencyForm, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-saffron focus:outline-none"
                      placeholder="email@example.com"
                    />
                    {agencyForm.email && !validateEmail(agencyForm.email) && (
                      <p className="mt-1 text-xs text-red-600">Invalid email address.</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input
                      type="text"
                      value={agencyForm.address}
                      onChange={(e) => setAgencyForm({ ...agencyForm, address: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-saffron focus:outline-none"
                      placeholder="Enter agency address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={agencyForm.city}
                      onChange={(e) => setAgencyForm({ ...agencyForm, city: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-saffron focus:outline-none"
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <input
                      type="text"
                      value={agencyForm.state}
                      onChange={(e) => setAgencyForm({ ...agencyForm, state: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-saffron focus:outline-none"
                      placeholder="State"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                    <input
                      type="text"
                      value={agencyForm.pincode}
                      onChange={(e) => setAgencyForm({ ...agencyForm, pincode: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-saffron focus:outline-none"
                      placeholder="000000"
                    />
                  </div>
                  {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
                    <input
                      type="url"
                      value={agencyForm.logo_url}
                      onChange={(e) => setAgencyForm({ ...agencyForm, logo_url: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-saffron focus:outline-none"
                      placeholder="https://..."
                    />
                  </div> */}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    disabled={updateAgencyMutation.isPending}
                    className="flex-1 bg-saffron hover:bg-orange-600"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                  >
                    <X className="w-4 h-4 mr-2 inline" />
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase mb-1">Agency Name</p>
                    <p className="text-gray-900">{agencyForm.name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase mb-1">License Number</p>
                    <p className="text-gray-900">{agencyForm.license_number || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase mb-1">Phone</p>
                    <p className="text-gray-900">{agencyForm.phone || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase mb-1">Email</p>
                    <p className="text-gray-900">{agencyForm.email || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase mb-1">Address</p>
                    <p className="text-gray-900">{agencyForm.address || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase mb-1">City</p>
                    <p className="text-gray-900">{agencyForm.city || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase mb-1">State</p>
                    <p className="text-gray-900">{agencyForm.state || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase mb-1">Pincode</p>
                    <p className="text-gray-900">{agencyForm.pincode || '—'}</p>
                  </div>
                  {/* <div>
                    <p className="text-xs text-gray-500 font-medium uppercase mb-1">Logo URL</p>
                    <p className="text-gray-900 truncate">{agencyForm.logo_url || '—'}</p>
                  </div> */}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Profile Settings */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">My Profile</h2>
            {editingSection !== 'profile' && (
              <button
                onClick={() => setEditingSection('profile')}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
            )}
          </div>

          {editingSection === 'profile' ? (
            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name*</label>
                <input
                  type="text"
                  value={profileForm.full_name}
                  onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-saffron focus:outline-none"
                  placeholder="Your full name"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  disabled={updateProfileMutation.isPending}
                  className="flex-1 bg-saffron hover:bg-orange-600"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                >
                  <X className="w-4 h-4 mr-2 inline" />
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase mb-1">Full Name</p>
                <p className="text-gray-900">{profileForm.full_name || '—'}</p>
              </div>
            </div>
          )}

          <div className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            {!showPasswordForm ? (
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-800 font-medium">Change Password</p>
                    <p className="text-sm text-blue-700 mt-1">Update your password to keep your account secure</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPasswordForm(true)}
                  className="ml-4 px-3 py-1 text-sm text-blue-600 hover:bg-blue-100 rounded transition flex-shrink-0"
                >
                  Change
                </button>
              </div>
            ) : (
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Password*</label>
                  <div className="relative">
                    <input
                      type={showPassword.old ? 'text' : 'password'}
                      value={passwordForm.old_password}
                      onChange={(e) =>
                        setPasswordForm({ ...passwordForm, old_password: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-saffron focus:outline-none"
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowPassword({ ...showPassword, old: !showPassword.old })
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword.old ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password*</label>
                  <div className="relative">
                    <input
                      type={showPassword.new ? 'text' : 'password'}
                      value={passwordForm.new_password}
                      onChange={(e) =>
                        setPasswordForm({ ...passwordForm, new_password: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-saffron focus:outline-none"
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowPassword({ ...showPassword, new: !showPassword.new })
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password*</label>
                  <div className="relative">
                    <input
                      type={showPassword.confirm ? 'text' : 'password'}
                      value={passwordForm.confirm_password}
                      onChange={(e) =>
                        setPasswordForm({ ...passwordForm, confirm_password: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-saffron focus:outline-none"
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowPassword({
                          ...showPassword,
                          confirm: !showPassword.confirm,
                        })
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {passwordForm.new_password && !validatePassword(passwordForm.new_password) && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800 font-medium mb-2">Password must contain:</p>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li>✓ At least 8 characters</li>
                      <li>✓ At least 1 uppercase letter (A-Z)</li>
                      <li>✓ At least 1 lowercase letter (a-z)</li>
                      <li>✓ At least 1 digit (0-9)</li>
                      <li>✓ At least 1 special character (!@#$%^&*)</li>
                    </ul>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    disabled={changePasswordMutation.isPending}
                    className="flex-1 bg-saffron hover:bg-orange-600"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Change Password
                  </Button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordForm(false)
                      setPasswordForm({
                        old_password: '',
                        new_password: '',
                        confirm_password: '',
                      })
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                  >
                    <X className="w-4 h-4 mr-2 inline" />
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowPaymentModal(false)}
            ></div>

            {/* Modal container */}
            <div className="relative inline-block transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all mx-auto mt-4 max-w-md w-full">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Pay Now
                </h3>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-6 space-y-4">
              {/* QR Code */}
                <div className="flex justify-center">
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <QRCodeCanvas
                      value={`upi://pay?pa=9766348810@upi&pn=RTO%20Services&am=${billingStatus?.price_display?.replace(/[^0-9.]/g, '') || '0'}&tn=RTO%20Agency%20Payment%20-%20${agency?.phone || 'Phone'}`}
                      size={200}
                      level="H"
                      includeMargin={false}
                    />
                  </div>
                </div>

                {/* Instructions */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 space-y-3">
                  <h4 className="font-semibold text-blue-900 text-sm">Payment Instructions:</h4>
                  <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                    <li>Scan the QR code with any UPI app</li>
                    <li>
                      <strong>Important:</strong> Add your agency phone number in the <strong>"Payment Note"</strong> field
                      <div className="mt-1 p-2 bg-white rounded border border-blue-300 text-xs text-gray-700">
                        Example: <code className="text-blue-600 font-mono">+91-XXXXXXXXXX</code>
                      </div>
                    </li>
                    <li>Complete the payment</li>
                    <li>Send the payment confirmation screenshot to WhatsApp</li>
                  </ol>
                </div>

                {/* WhatsApp Contact */}
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <p className="text-sm text-green-800 mb-3">
                    After payment, share the confirmation with us on WhatsApp to activate your agency immediately.
                  </p>
                </div>

                {/* Payment Details */}
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <p className="text-xs text-gray-600 mb-2">UPI Payment Details:</p>
                  <div className="space-y-1 text-sm text-gray-700 font-mono">
                    <p><strong>UPI ID:</strong> 9766348810@upi</p>
                    <p><strong>Name:</strong> RTO Services</p>
                    <p><strong>Note:</strong> Add your phone number in payment note</p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

